import { ThunkDispatch } from "@reduxjs/toolkit";
import React, { useState, useRef, useCallback, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Stage, Layer, Image as KonvaImage, Rect, Line, Transformer, Group } from "react-konva";

import {
  floorPlanExtractionActions,
  FloorPlanImage,
  FloorPlanAfterImage,
  FloorPlanStyle,
  SelectionMode,
  SelectionArea,
} from "../../reducers/floorPlanExtraction";
import { isPDFFile, convertPDFPageToImage, getPDFInfo, clearPDFCache } from "../../utils/pdfToImage";
import { fetchImageAsBlob } from "../../utils/watermark";
import { toastActions } from "../../reducers/toast";
import { PDFPageSelectModal } from "./ui/PDFPageSelectModal";
import { DownloadOptionPopup } from "./ui/DownloadOptionPopup";
import { selectCanProcessFloorPlan, selectFloorPlanAfterImageView, selectFloorPlanAfterImageData } from "../../selectors";
import { SelectFileIcon, CloseModalIcon, SquareOutlineIcon, LeadPencilIcon, ConfirmGenerateAfterPhotoIcon, GenerateAfterPhotoIcon } from "../SVGIcons";
import { HintPopup } from "../common/HintPopup";
import { AIProcessingPopup } from "../common/AIProcessingPopup";
import { Modal } from "flowbite-react";
import floor_plan_normal from '../../../assets/images/floor_plan_normal.png';
import floor_plan_colored from '../../../assets/images/floor_plan_colored.png';
import floor_plan_handdrawn from '../../../assets/images/floor_plan_handdrawn.png';

// State型定義
interface RootState {
  floorPlanExtraction: {
    isLoading: boolean;
    sourceImage: FloorPlanImage | null;
    afterImages: FloorPlanAfterImage[];
    selectedAfterImage?: number;
    selectionMode: SelectionMode;
    selectionArea: SelectionArea | null;
    selectedStyle: FloorPlanStyle;
    drawingLines: number[][];
    runTime: number;
    maxExecTime: number;
    isPhotoTooBigPopupShow: boolean;
    showConfirmPopup: boolean;
    [key: string]: unknown;
  };
  currentUser: {
    creditConsumed: number;
    creditLimit: number;
    isLimitInfinity: boolean;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

// mapStateToPropsの型定義
function mapStateToProps(state: RootState) {
  const canProcess = selectCanProcessFloorPlan(state);
  const viewState = selectFloorPlanAfterImageView(state);
  const afterImageData = selectFloorPlanAfterImageData(state);
  return {
    ...state.floorPlanExtraction,
    ...state.currentUser,
    canProcess,
    viewState,
    afterImageData,
  };
}

// コンポーネントのProps型定義
interface FloorPlanFormSomeOwnProps {
  onUseForBandReplacement?: () => void;
  hasExtractionResult?: boolean;
}

interface FloorPlanFormSomeProps extends FloorPlanFormSomeOwnProps {
  isLoading: boolean;
  sourceImage: FloorPlanImage | null;
  afterImages: FloorPlanAfterImage[];
  selectedAfterImage?: number;
  selectionMode: SelectionMode;
  selectionArea: SelectionArea | null;
  selectedStyle: FloorPlanStyle;
  drawingLines: number[][];
  runTime: number;
  maxExecTime: number;
  isPhotoTooBigPopupShow: boolean;
  showConfirmPopup: boolean;
  canProcess: boolean;
  viewState: string;
  afterImageData: FloorPlanAfterImage | null;
  creditConsumed: number;
  creditLimit: number;
  isLimitInfinity: boolean;
}

// スタイルオプション
// TODO: 画像はテスト用。正式な画像に差し替える必要あり
const styleOptions: { value: FloorPlanStyle; label: string; description: string; image: string }[] = [
  { value: 'normal', label: '元画像のまま（加工なし）', description: 'そのまま抜き出し', image: floor_plan_normal },
  { value: 'colored', label: '色付け', description: '色を付けて出力', image: floor_plan_colored },
  { value: 'handdrawn', label: '手書き風', description: '手描きイラスト風', image: floor_plan_handdrawn },
  // { value: '3d', label: '3D', description: '立体的な間取り図', image: pho_oneroom },
];

const FloorPlanFormSome = (props: FloorPlanFormSomeProps) => {
  const {
    isLoading,
    sourceImage,
    afterImages,
    selectedAfterImage,
    selectionMode,
    selectionArea,
    selectedStyle,
    drawingLines,
    isPhotoTooBigPopupShow,
    showConfirmPopup,
    canProcess,
    viewState,
    afterImageData,
    onUseForBandReplacement,
    hasExtractionResult,
    creditConsumed,
    creditLimit,
    isLimitInfinity,
  } = props;

  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const navigate = useNavigate();

  // Canvas用のstate
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const isDrawingRef = useRef(false); // 同期的な描画状態管理
  const [rectStart, setRectStart] = useState<{ x: number; y: number } | null>(null);
  const [currentRect, setCurrentRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const currentRectRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);
  const [currentLine, setCurrentLine] = useState<number[]>([]);
  const currentLineRef = useRef<number[]>([]);
  const stageRef = useRef<any>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const selectionRectRef = useRef<any>(null);
  const selectionLineRef = useRef<any>(null);
  const transformerRef = useRef<any>(null);
  const [isSelectionSelected, setIsSelectionSelected] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false); // 同期的なドラッグ状態管理
  // ドラッグ中の位置を管理（Reactの再レンダリングによる位置リセットを防ぐ）
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);

  // Before/After表示切り替え
  const [showAfter, setShowAfter] = useState(false);

  // スタイル選択ポップアップ
  const [isStylePopupShow, setIsStylePopupShow] = useState(false);

  // キャンセル確認ポップアップ
  const [isCancelConfirmShow, setIsCancelConfirmShow] = useState(false);

  // ダウンロードモーダル
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // PDF関連の状態
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [isProcessingPdf, setIsProcessingPdf] = useState(false);

  // Canvas dimensions
  const [canvasSize, setCanvasSize] = useState({ width: 600, height: 400 });

  // キャンバスサイズを計算
  const calculateCanvasSize = useCallback((img: HTMLImageElement) => {
    if (!canvasContainerRef.current) return { width: 600, height: 400 };

    const containerRect = canvasContainerRef.current.getBoundingClientRect();
    const maxWidth = containerRect.width - 20; // パディング分を引く
    const maxHeight = containerRect.height - 20;

    const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
    return {
      width: Math.floor(img.width * scale),
      height: Math.floor(img.height * scale),
    };
  }, []);

  // 画像読み込み
  useEffect(() => {
    if (sourceImage?.uri) {
      const img = new window.Image();
      img.src = sourceImage.uri;
      img.onload = () => {
        setImage(img);
        setCanvasSize(calculateCanvasSize(img));
      };
    }
  }, [sourceImage?.uri, calculateCanvasSize]);

  // ウィンドウリサイズ時にキャンバスサイズを再計算
  useEffect(() => {
    const handleResize = () => {
      if (image) {
        setCanvasSize(calculateCanvasSize(image));
      }
    };

    window.addEventListener('resize', handleResize);
    // 初回レンダリング後にサイズを再計算
    const timer = setTimeout(handleResize, 100);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, [image, calculateCanvasSize]);

  // Transformerを選択範囲に適用
  useEffect(() => {
    if (transformerRef.current) {
      if (isSelectionSelected && selectionArea) {
        if (selectionArea.type === 'rectangle' && selectionRectRef.current) {
          transformerRef.current.nodes([selectionRectRef.current]);
        } else if (selectionArea.type === 'freehand' && selectionLineRef.current) {
          transformerRef.current.nodes([selectionLineRef.current]);
        }
      } else {
        transformerRef.current.nodes([]);
      }
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [isSelectionSelected, selectionArea]);

  // API成功時（afterImagesに新しい画像が追加された時）にAfter表示に切り替え
  const prevAfterImagesLengthRef = useRef(afterImages.length);
  useEffect(() => {
    if (afterImages.length > prevAfterImagesLengthRef.current) {
      setShowAfter(true);
    }
    prevAfterImagesLengthRef.current = afterImages.length;
  }, [afterImages.length]);


  // 選択範囲上でマウスダウン（新しい選択を開始）
  const handleSelectionMouseDown = (e: any) => {
    // 既存の選択上でもマウスダウンすると新しい選択を開始
    // これにより「クリックして消す→再描画」の手間を省く
    handleMouseDown(e);
  };

  // 選択範囲をクリック（選択状態にする）- 現在は使用しない
  const handleSelectionClick = (e: any) => {
    e.cancelBubble = true; // Konvaイベントの伝播を停止
    setIsSelectionSelected(true);
  };

  // ドラッグ開始
  const handleDragStart = (e: any) => {
    isDraggingRef.current = true; // 同期的に更新
    setIsDragging(true);
    const node = e.target;
    setDragPosition({ x: node.x(), y: node.y() });
  };

  // ドラッグ中
  const handleDragMove = (e: any) => {
    const node = e.target;
    setDragPosition({ x: node.x(), y: node.y() });
  };

  // 矩形のドラッグ終了
  const handleRectDragEnd = (e: any) => {
    isDraggingRef.current = false; // 同期的に更新
    setIsDragging(false);
    setDragPosition(null);
    if (!selectionArea || selectionArea.type !== 'rectangle') return;
    const node = e.target;
    const newArea: SelectionArea = {
      type: 'rectangle',
      x: node.x(),
      y: node.y(),
      width: selectionArea.width,
      height: selectionArea.height,
    };
    dispatch(floorPlanExtractionActions.setSelectionArea(newArea));
    generateSelectionMask(newArea);
  };

  // 矩形のトランスフォーム終了
  const handleRectTransformEnd = (e: any) => {
    if (!selectionArea || selectionArea.type !== 'rectangle') return;
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    // スケールをリセットして実際のサイズに反映
    node.scaleX(1);
    node.scaleY(1);

    const newArea: SelectionArea = {
      type: 'rectangle',
      x: node.x(),
      y: node.y(),
      width: Math.max(10, (selectionArea.width || 0) * scaleX),
      height: Math.max(10, (selectionArea.height || 0) * scaleY),
    };
    dispatch(floorPlanExtractionActions.setSelectionArea(newArea));
    generateSelectionMask(newArea);
  };

  // フリーハンドのドラッグ終了
  const handleLineDragEnd = (e: any) => {
    isDraggingRef.current = false; // 同期的に更新
    setIsDragging(false);
    setDragPosition(null);
    if (!selectionArea || selectionArea.type !== 'freehand' || !selectionArea.points) return;
    const node = e.target;
    const dx = node.x();
    const dy = node.y();

    // 全てのポイントを移動
    const newPoints = selectionArea.points.map((p, i) =>
      i % 2 === 0 ? p + dx : p + dy
    );

    // ノードの位置をリセット
    node.x(0);
    node.y(0);

    const newArea: SelectionArea = {
      type: 'freehand',
      points: newPoints,
    };
    dispatch(floorPlanExtractionActions.setSelectionArea(newArea));
    dispatch(floorPlanExtractionActions.setDrawingLines([newPoints]));
    generateSelectionMask(newArea);
  };

  // 選択モードを変更
  const handleSelectionModeChange = (mode: SelectionMode) => {
    dispatch(floorPlanExtractionActions.setSelectionMode(mode));
    dispatch(floorPlanExtractionActions.clearSelection());
    setCurrentRect(null);
    setCurrentLine([]);
    setIsSelectionSelected(false);
    // After表示中の場合はBefore表示に切り替え
    setShowAfter(false);
  };

  // スタイルを変更
  const handleStyleChange = (style: FloorPlanStyle) => {
    dispatch(floorPlanExtractionActions.setSelectedStyle(style));
  };

  // マウス/タッチイベント（矩形選択） - 背景画像からのみ呼ばれる
  const handleMouseDown = (e: any) => {
    if (isLoading || isDraggingRef.current) return;

    // 背景画像がクリックされたので、選択解除して新しい描画を開始
    setIsSelectionSelected(false);

    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    isDrawingRef.current = true; // 同期的に更新
    setIsDrawing(true);

    // 新しい選択を開始する前に既存の選択をクリア（1つだけ選択可能にする）
    dispatch(floorPlanExtractionActions.setSelectionArea(null));
    dispatch(floorPlanExtractionActions.clearDrawingLines());

    if (selectionMode === 'rectangle') {
      setRectStart(point);
      const initRect = { x: point.x, y: point.y, width: 0, height: 0 };
      currentRectRef.current = initRect;
      setCurrentRect(initRect);
    } else {
      const initLine = [point.x, point.y];
      currentLineRef.current = initLine;
      setCurrentLine(initLine);
    }
  };

  // 選択範囲の最小サイズ（ピクセル）
  const MIN_SELECTION_SIZE = 50;

  const handleMouseMove = (e: any) => {
    if (!isDrawingRef.current || isLoading || isDraggingRef.current) return;
    const stage = e.target.getStage();
    const point = stage.getPointerPosition();

    if (selectionMode === 'rectangle' && rectStart) {
      let width = Math.abs(point.x - rectStart.x);
      let height = Math.abs(point.y - rectStart.y);

      // 最小サイズを適用
      if (width < MIN_SELECTION_SIZE) {
        width = MIN_SELECTION_SIZE;
      }
      if (height < MIN_SELECTION_SIZE) {
        height = MIN_SELECTION_SIZE;
      }

      const x = point.x < rectStart.x ? rectStart.x - width : rectStart.x;
      const y = point.y < rectStart.y ? rectStart.y - height : rectStart.y;

      const rect = { x, y, width, height };
      currentRectRef.current = rect;
      setCurrentRect(rect);
    } else if (selectionMode === 'freehand') {
      const newLine = [...currentLineRef.current, point.x, point.y];
      currentLineRef.current = newLine;
      setCurrentLine(newLine);
    }
  };

  const handleMouseUp = () => {
    if (!isDrawingRef.current || isDraggingRef.current) return;
    isDrawingRef.current = false; // 同期的に更新
    setIsDrawing(false);

    const rect = currentRectRef.current;
    const line = currentLineRef.current;

    if (selectionMode === 'rectangle' && rect) {
      // 矩形選択の場合は最小サイズが適用されているので、常に有効
      if (rect.width >= MIN_SELECTION_SIZE && rect.height >= MIN_SELECTION_SIZE) {
        const area: SelectionArea = {
          type: 'rectangle',
          ...rect,
        };
        dispatch(floorPlanExtractionActions.setSelectionArea(area));
        generateSelectionMask(area);
        // 描画後に自動的に選択状態にする
        setIsSelectionSelected(true);
      }
    } else if (selectionMode === 'freehand' && line.length > 4) {
      // フリーハンドの場合、バウンディングボックスのサイズをチェック
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (let i = 0; i < line.length; i += 2) {
        minX = Math.min(minX, line[i]);
        maxX = Math.max(maxX, line[i]);
        minY = Math.min(minY, line[i + 1]);
        maxY = Math.max(maxY, line[i + 1]);
      }
      const width = maxX - minX;
      const height = maxY - minY;

      let scaledLine = line;

      // 最小サイズより小さい場合はスケールアップ
      if (width < MIN_SELECTION_SIZE || height < MIN_SELECTION_SIZE) {
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;
        // 幅と高さの小さい方に合わせてスケール係数を計算
        const scaleX = width > 0 ? MIN_SELECTION_SIZE / width : 1;
        const scaleY = height > 0 ? MIN_SELECTION_SIZE / height : 1;
        const scale = Math.max(scaleX, scaleY);

        // 各ポイントを中心を基準にスケールアップ
        scaledLine = [];
        for (let i = 0; i < line.length; i += 2) {
          const newX = centerX + (line[i] - centerX) * scale;
          const newY = centerY + (line[i + 1] - centerY) * scale;
          scaledLine.push(newX, newY);
        }
      }

      const area: SelectionArea = {
        type: 'freehand',
        points: scaledLine,
      };
      dispatch(floorPlanExtractionActions.setSelectionArea(area));
      dispatch(floorPlanExtractionActions.addDrawingLine(scaledLine));
      generateSelectionMask(area);
      // 描画後に自動的に選択状態にする
      setIsSelectionSelected(true);
    }
    setRectStart(null);
    currentRectRef.current = null;
    currentLineRef.current = [];
    setCurrentRect(null);
    setCurrentLine([]);
  };

  // 選択範囲のマスク画像を生成
  const generateSelectionMask = useCallback((area: SelectionArea) => {
    if (!image || !stageRef.current) return;

    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 背景を黒で塗りつぶし
    ctx.fillStyle = 'black';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // スケール計算
    const scaleX = image.width / canvasSize.width;
    const scaleY = image.height / canvasSize.height;

    // 選択範囲を白で塗りつぶし
    ctx.fillStyle = 'white';
    if (area.type === 'rectangle' && area.x !== undefined && area.y !== undefined && area.width !== undefined && area.height !== undefined) {
      ctx.fillRect(
        area.x * scaleX,
        area.y * scaleY,
        area.width * scaleX,
        area.height * scaleY
      );
    } else if (area.type === 'freehand' && area.points && area.points.length > 4) {
      ctx.beginPath();
      ctx.moveTo(area.points[0] * scaleX, area.points[1] * scaleY);
      for (let i = 2; i < area.points.length; i += 2) {
        ctx.lineTo(area.points[i] * scaleX, area.points[i + 1] * scaleY);
      }
      ctx.closePath();
      ctx.fill();
    }

    // BlobとURLを生成
    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        dispatch(floorPlanExtractionActions.setSelectionPreviewUrl(url));
        dispatch(floorPlanExtractionActions.setSelectionPreviewBlob(blob));
      }
    }, 'image/png');
  }, [image, canvasSize, dispatch]);

  // 選択をクリア
  const handleClearSelection = () => {
    dispatch(floorPlanExtractionActions.clearSelection());
    setCurrentRect(null);
    setCurrentLine([]);
    setIsSelectionSelected(false);
  };

  // テスト用プレビュー画像のCanvasを生成（選択範囲を切り出す）
  const generateCroppedCanvas = useCallback((): HTMLCanvasElement | null => {
    if (!image || !selectionArea) return null;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // スケール計算
    const scaleX = image.width / canvasSize.width;
    const scaleY = image.height / canvasSize.height;

    if (selectionArea.type === 'rectangle' && selectionArea.x !== undefined && selectionArea.width !== undefined) {
      // 矩形選択の場合：選択範囲を切り出し
      const srcX = selectionArea.x * scaleX;
      const srcY = (selectionArea.y || 0) * scaleY;
      const srcWidth = selectionArea.width * scaleX;
      const srcHeight = (selectionArea.height || 0) * scaleY;

      canvas.width = srcWidth;
      canvas.height = srcHeight;

      // 背景は透明のまま
      ctx.drawImage(image, srcX, srcY, srcWidth, srcHeight, 0, 0, srcWidth, srcHeight);
    } else if (selectionArea.type === 'freehand' && selectionArea.points && selectionArea.points.length > 4) {
      // フリーハンドの場合：バウンディングボックスで切り出し
      const points = selectionArea.points;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (let i = 0; i < points.length; i += 2) {
        minX = Math.min(minX, points[i]);
        maxX = Math.max(maxX, points[i]);
        minY = Math.min(minY, points[i + 1]);
        maxY = Math.max(maxY, points[i + 1]);
      }

      const srcX = minX * scaleX;
      const srcY = minY * scaleY;
      const srcWidth = (maxX - minX) * scaleX;
      const srcHeight = (maxY - minY) * scaleY;

      canvas.width = srcWidth;
      canvas.height = srcHeight;

      // 背景は透明のまま、クリッピングパスを設定
      ctx.save();
      ctx.beginPath();
      ctx.moveTo((points[0] - minX) * scaleX, (points[1] - minY) * scaleY);
      for (let i = 2; i < points.length; i += 2) {
        ctx.lineTo((points[i] - minX) * scaleX, (points[i + 1] - minY) * scaleY);
      }
      ctx.closePath();
      ctx.clip();

      ctx.drawImage(image, srcX, srcY, srcWidth, srcHeight, 0, 0, srcWidth, srcHeight);
      ctx.restore();
    } else {
      return null;
    }

    return canvas;
  }, [image, selectionArea, canvasSize]);

  // テスト用プレビュー画像を生成（dataURL）
  const generateTestPreview = useCallback(() => {
    const canvas = generateCroppedCanvas();
    if (!canvas) return null;
    return canvas.toDataURL('image/png');
  }, [generateCroppedCanvas]);

  // 確認ポップアップを表示
  const handleShowConfirmPopup = () => {
    dispatch(floorPlanExtractionActions.setShowConfirmPopup(true));
  };

  // 確認ポップアップを閉じる
  const handleCloseConfirmPopup = () => {
    dispatch(floorPlanExtractionActions.setShowConfirmPopup(false));
  };

  // 処理実行（確認後）
  const handleProcess = async () => {
    // ポップアップを閉じる
    dispatch(floorPlanExtractionActions.setShowConfirmPopup(false));

    // トリミングしたcanvasを取得
    const croppedCanvas = generateCroppedCanvas();

    if (croppedCanvas) {
      // canvasをBlobに変換してAPI処理を実行（PNG形式で透明背景を保持）
      croppedCanvas.toBlob((blob) => {
        dispatch(floorPlanExtractionActions.processFloorPlanExtraction({
          navigate,
          croppedImageBlob: blob || undefined,
        }));
      }, 'image/png');
    } else {
      // トリミング失敗時はそのままAPI処理を実行
      dispatch(floorPlanExtractionActions.processFloorPlanExtraction({ navigate }));
    }
  };

  // キャンセル確認ポップアップを表示
  const handleShowCancelConfirm = () => {
    setIsCancelConfirmShow(true);
  };

  // キャンセルを実行（確認後）
  const handleConfirmCancel = () => {
    dispatch(floorPlanExtractionActions.clearData());
    clearPDFCache(); // PDFキャッシュもクリア
    setPdfFile(null); // PDFファイル状態もクリア
    setShowAfter(false);
    setIsCancelConfirmShow(false);
  };

  // キャンセル確認ポップアップを閉じる
  const handleCloseCancelConfirm = () => {
    setIsCancelConfirmShow(false);
  };

  // ファイル処理のメイン関数（PDF対応）
  async function handleFile(file: File) {
    if (isPDFFile(file)) {
      // PDFの場合はすぐにモーダルを表示（ページ数チェックはモーダル内で行う）
      setPdfFile(file);
      setIsPdfModalOpen(true);
    } else {
      // 画像の場合は従来通り
      dispatch(floorPlanExtractionActions.addSourceImage(file));
    }
  }

  // PDFページを画像に変換して追加
  const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

  async function processPdfPage(file: File, pageNumber: number) {
    setIsProcessingPdf(true);
    try {
      // 初期スケールで変換を試みる
      let scale = 2.0;
      let result = await convertPDFPageToImage(file, pageNumber, scale);

      // 5MB超えの場合はスケールを下げて再変換
      while (result.blob.size > MAX_IMAGE_SIZE && scale > 0.5) {
        console.log(`画像サイズが${(result.blob.size / 1024 / 1024).toFixed(2)}MBのため、スケールを${scale}から${scale - 0.5}に下げて再変換`);
        scale -= 0.5;
        result = await convertPDFPageToImage(file, pageNumber, scale);
      }

      // それでも5MB超えの場合はエラー
      if (result.blob.size > MAX_IMAGE_SIZE) {
        console.error(`変換後の画像サイズが${(result.blob.size / 1024 / 1024).toFixed(2)}MBで上限を超えています`);
        alert('PDFの解像度が高すぎるため、5MB以下に変換できませんでした。');
        return;
      }

      console.log(`PDF変換完了: scale=${scale}, size=${(result.blob.size / 1024 / 1024).toFixed(2)}MB`);

      // BlobからFileオブジェクトを作成
      const imageFile = new File([result.blob], `${file.name.replace('.pdf', '')}_page${pageNumber}.png`, {
        type: 'image/png',
      });
      dispatch(floorPlanExtractionActions.addSourceImage(imageFile));
    } catch (e) {
      console.error('Failed to convert PDF page:', e);
    } finally {
      setIsProcessingPdf(false);
    }
  }

  async function handlePdfPagesSelect(pageNumbers: number[]) {
    if (pdfFile) {
      // ファイルを保持してからモーダルを閉じる
      const file = pdfFile;
      setIsPdfModalOpen(false);
      setPdfFile(null);
      // 間取り抜き出しは1画像のみ対応のため、最初のページを使用
      await processPdfPage(file, pageNumbers[0]);
    }
  }

  function closePdfModal() {
    setIsPdfModalOpen(false);
    setPdfFile(null);
    clearPDFCache(); // PDFキャッシュもクリア
  }

  // ファイルアップロード
  const addSourceImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isLoading || !event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];
    // 同じファイルを再選択できるようにvalueをリセット
    event.target.value = '';
    handleFile(file);
  };

  // ドラッグ＆ドロップ
  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    if (isLoading) return;

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      handleFile(file);
    }
  };

  // ファイルサイズ超過ポップアップを閉じる
  const closePhotoTooBigPopup = () => {
    dispatch(floorPlanExtractionActions.setIsPhotoTooBigPopupShow(false));
  };

  // ダウンロードモーダルを開く
  const handleOpenDownloadModal = () => {
    setShowDownloadModal(true);
  };

  // ダウンロードモーダルを閉じる
  const handleCloseDownloadModal = () => {
    setShowDownloadModal(false);
  };

  // ダウンロード用の画像URLとファイル名を取得
  const getDownloadImageInfo = () => {
    if (showAfter && afterImageData?.serverURI) {
      return {
        imageUrl: afterImageData.serverURI,
        fileName: afterImageData.fileName || 'floor_plan.png',
      };
    } else if (!showAfter && sourceImage?.uri) {
      return {
        imageUrl: sourceImage.uri,
        fileName: sourceImage.file.name || 'original.png',
      };
    }
    return { imageUrl: undefined, fileName: 'image.png' };
  };

  const downloadImageInfo = getDownloadImageInfo();

  return (
    <div className="border border-gray-300 bg-gray-100 rounded">
      {/* PDFページ選択モーダル */}
      <PDFPageSelectModal
        isOpen={isPdfModalOpen}
        file={pdfFile}
        onSelectPages={handlePdfPagesSelect}
        onClose={closePdfModal}
        maxSelectCount={1}
      />

      {/* PDF処理中オーバーレイ */}
      {isProcessingPdf && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span>PDFを変換中...</span>
          </div>
        </div>
      )}

      {/* ファイルサイズ超過ポップアップ */}
      {isPhotoTooBigPopupShow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">ファイルサイズエラー</h3>
            <p className="text-gray-600 mb-4">
              ファイルサイズが大きすぎます。5MB以下の画像をアップロードしてください。
            </p>
            <button
              onClick={closePhotoTooBigPopup}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {/* クレジット消費確認ポップアップ */}
      <Modal show={showConfirmPopup} onClose={handleCloseConfirmPopup} size="md">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <button type="button" onClick={handleCloseConfirmPopup}
                className="absolute top-3 end-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center">
                <CloseModalIcon />
                <span className="sr-only">閉じる</span>
              </button>
              <div className="p-4 md:p-5">
                <ConfirmGenerateAfterPhotoIcon />
                <h3 className="text-base font-semibold leading-6 text-gray-900">間取り抜き出しに1回分のクレジットを使用します。よろしいですか？<br/><br/></h3>
                <div className="flex justify-between mb-1 text-gray-500">
                  <span className="text-base font-normal">実行回数</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {isLimitInfinity
                      ? `実行回数 ${creditConsumed}回/上限なし(アカウントのクレジット上限)`
                      : `残り${creditLimit - creditConsumed}回/${creditLimit}回`
                    }
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-orange-500 h-2.5 rounded-full" style={{width: isLimitInfinity ? '0%' : `${(creditConsumed / creditLimit * 100)}%`}}></div>
                </div>
                <div className="flex flex-row-reverse items-center mt-6 space-x-4">
                  <button type="button" onClick={handleProcess}
                    className="sm:ml-3 w-28 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center">抜き出し</button>
                  <button type="button" onClick={handleCloseConfirmPopup}
                    className="sm:ml-3 w-28 py-2.5 px-5 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-300 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100">キャンセル</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* クリア確認ポップアップ */}
      <HintPopup
        show={isCancelConfirmShow}
        title="全ての画像を削除"
        description={
          <div className="space-y-3">
            <p>アップロード済み画像と生成した画像全てを削除します。</p>
            <p className="text-red-600 font-medium">
              この操作は取り消せません。
            </p>
          </div>
        }
        onClose={handleCloseCancelConfirm}
        showExecuteButton={true}
        onExecute={handleConfirmCancel}
        executeButtonText="削除"
      />

      {/* 間取り抜き出し処理中ポップアップ */}
      <AIProcessingPopup
        show={isLoading}
        title="間取り抜き出しを実行中..."
        description="AIが間取りを抜き出しています。"
        subDescription="しばらくお待ちください..."
      />

      {/* スタイル選択ポップアップ */}
      <Modal show={isStylePopupShow} onClose={() => setIsStylePopupShow(false)}>
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t">
                <h3 className="text-xl font-semibold text-gray-900">
                  スタイル選択
                </h3>
                <button
                  type="button"
                  onClick={() => setIsStylePopupShow(false)}
                  className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
                >
                  <CloseModalIcon />
                  <span className="sr-only">Close modal</span>
                </button>
              </div>
              <div className="p-4 md:p-5 space-y-4">
                <div className="grid gap-3 grid-cols-2">
                  {styleOptions.map((option) => (
                    <div key={option.value} className="relative col-1">
                      <label
                        onClick={() => {
                          handleStyleChange(option.value);
                          setIsStylePopupShow(false);
                        }}
                        className={selectedStyle === option.value
                          ? "border-2 border-orange-500 bg-orange-50 flex cursor-pointer select-none overflow-hidden h-full group rounded-lg"
                          : "bg-white border border-gray-200 flex cursor-pointer select-none overflow-hidden h-full group rounded-lg hover:bg-gray-50"
                        }
                      >
                        <span className="bg-[#ff4600cc] absolute top-1/2 -mt-6 inset-x-0 p-2 text-center font-bold text-white">
                          {option.label}
                        </span>
                        <figure>
                          <img src={option.image} alt={option.label} className="w-full h-auto object-cover" />
                        </figure>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* ダウンロード設定ポップアップ */}
      <DownloadOptionPopup
        show={showDownloadModal}
        onClose={handleCloseDownloadModal}
        imageUrl={downloadImageInfo.imageUrl}
        defaultFileName={downloadImageInfo.fileName}
        onDownloadSuccess={() => dispatch(toastActions.toastSuccess('画像のダウンロードを開始しました'))}
        onDownloadError={() => dispatch(toastActions.toastError('画像のダウンロードに失敗しました'))}
      />

      <div className="flex gap-3 p-3" style={{ height: 'calc(100vh - 340px)', minHeight: '400px' }}>
          {/* 左側：スタイル・設定パネル（常に表示） */}
          <div className="flex-shrink-0 bg-white rounded-lg p-3 flex flex-col" style={{ width: '220px', minWidth: '220px', maxWidth: '220px' }}>
            <h3 className="text-sm font-semibold mb-2 text-gray-700">スタイル</h3>
            <button
              onClick={() => !isLoading && setIsStylePopupShow(true)}
              disabled={isLoading}
              className="w-full p-2 text-sm border border-gray-300 rounded-md mb-4 bg-gray-50 text-gray-900 text-left focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {styleOptions.find(opt => opt.value === selectedStyle)?.label || '未選択'}
            </button>

            <h3 className="text-sm font-semibold mb-2 text-gray-700">選択モード</h3>
            <div className="flex space-x-2 mb-2">
              <button
                onClick={() => handleSelectionModeChange('rectangle')}
                disabled={isLoading}
                title="四角で選択"
                className={`flex-1 p-2 rounded flex items-center justify-center ${
                  selectionMode === 'rectangle'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <SquareOutlineIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleSelectionModeChange('freehand')}
                disabled={isLoading}
                title="手書きで選択"
                className={`flex-1 p-2 rounded flex items-center justify-center ${
                  selectionMode === 'freehand'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <LeadPencilIcon className="w-5 h-5" />
              </button>
            </div>
            {!selectionArea && (
              <p className="text-xs text-gray-400 mb-4 text-center">
                範囲を選択してください
              </p>
            )}
            {selectionArea && <div className="mb-4" />}

            {/* スペーサー */}
            <div className="flex-grow" />

            {/* 実行ボタン */}
            <button
              onClick={handleShowConfirmPopup}
              disabled={!selectionArea || isLoading || !canProcess}
              className={`w-full px-3 py-2 rounded-md text-white font-medium text-sm flex items-center justify-center gap-1 ${
                selectionArea && !isLoading && canProcess
                  ? 'submit-btn hover:submit-btn'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
                {!isLoading && <GenerateAfterPhotoIcon />}
              {isLoading ? '処理中...' : '抜き出す'}
              {!isLoading && (
                <span
                  className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold rounded-full"
                  style={selectionArea && canProcess
                    ? { backgroundColor: '#ffd2c1', color: '#ff4500' }
                    : { backgroundColor: '#e5e7eb', color: '#9ca3af' }
                  }
                >
                  1
                </span>
              )}
            </button>

            <button
              onClick={handleShowCancelConfirm}
              className="w-full mt-2 px-3 py-2 text-sm font-medium bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              disabled={isLoading}
            >
              クリア
            </button>
          </div>

          {/* 中央：元画像と選択キャンバス / 結果画像 */}
          <div className="flex-grow bg-white rounded-lg p-3 flex flex-col min-w-0">
            <div className="flex items-center mb-2 gap-4">
              {/* 説明文 */}
              <p className="text-xs text-gray-500">
                画像から間取りを抜き出し、AIでデザインを変更できます
              </p>
            </div>

            {/* Canvasエリア / After画像表示エリア */}
            <div ref={canvasContainerRef} className="relative flex-grow border border-gray-300 rounded bg-gray-50 flex items-center justify-center overflow-hidden">
              {/* Before/After切り替えボタン - 右上に配置 */}
              <div className="absolute top-0 right-0 p-4 z-50">
                <button
                  onClick={() => {
                    // 結果がない場合はAfterに切り替えない
                    if (!showAfter && afterImages.length === 0) return;
                    setShowAfter(!showAfter);
                  }}
                  disabled={!showAfter && afterImages.length === 0}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '9999px',
                    fontWeight: 'bold',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s',
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    border: '2px solid #d1d5db',
                    cursor: (!showAfter && afterImages.length === 0) ? 'not-allowed' : 'pointer',
                    opacity: (!showAfter && afterImages.length === 0) ? 0.5 : 1,
                  }}
                  type="button"
                >
                  {showAfter ? 'After' : 'Before'}
                </button>
              </div>

              {/* After表示モード */}
              {showAfter ? (
                <div className="w-full h-full flex items-center justify-center p-4">
                  {(() => {
                    // 選択中の結果画像を取得
                    const selectedAfter = afterImages.find(a => a.id === selectedAfterImage) ||
                      (afterImages.length > 0 ? afterImages[afterImages.length - 1] : null);
                    const displayUrl = selectedAfter?.serverURI;

                    if (displayUrl) {
                      return (
                        <img
                          src={displayUrl}
                          alt="抜き出し結果"
                          className="max-w-full max-h-full object-contain"
                        />
                      );
                    }
                    return (
                      <div className="text-center text-gray-500">
                        <p className="text-base font-medium mb-2">結果画像がありません</p>
                        <p className="text-sm">Beforeタブで範囲を選択し、抜き出しを実行してください</p>
                      </div>
                    );
                  })()}
                </div>
              ) : !sourceImage ? (
                // 画像がない場合はアップロードエリアを表示
                <label
                  className="flex flex-col items-center justify-center w-full h-full border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                  htmlFor="floor-plan-file-input"
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6 space-y-3">
                    <SelectFileIcon className="w-12 h-12 text-gray-500" />
                    <p className="mb-2 text-sm text-gray-500">
                      <strong className="font-semibold text-blue-600">
                        マイソク画像を選択、またはドラッグ＆ドロップ
                      </strong>
                    </p>
                    <p className="text-xs text-gray-500">
                      JPEG, PNG, WebP, PDFが使用可能（5MBまで）
                    </p>
                    <div className="px-4 py-2 focus:ring-blue-500 focus:border-blue-500 whitespace-nowrap text-sm font-medium rounded-md text-white hover:text-white bg-blue-600 hover:bg-blue-700 w-auto min-w-28 text-center">
                      画像を選択
                    </div>
                  </div>
                  <input
                    accept="image/png, image/jpeg, image/webp, application/pdf, .pdf"
                    aria-label="マイソク画像を選択"
                    className="hidden"
                    id="floor-plan-file-input"
                    multiple={false}
                    onChange={addSourceImage}
                    type="file"
                  />
                </label>
              ) : image ? (
                <Stage
                  ref={stageRef}
                  width={canvasSize.width}
                  height={canvasSize.height}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onTouchMove={handleMouseMove}
                  onTouchEnd={handleMouseUp}
                  style={{ cursor: isLoading ? 'not-allowed' : (isDragging ? 'move' : (isSelectionSelected ? 'move' : 'crosshair')) }}
                >
                  <Layer>
                    {/* 背景画像 - マウスダウンのみここで処理（新しい描画開始用） */}
                    <KonvaImage
                      image={image}
                      width={canvasSize.width}
                      height={canvasSize.height}
                      onMouseDown={handleMouseDown}
                      onTouchStart={handleMouseDown}
                    />
                    {/* 確定した選択範囲（矩形） */}
                    {selectionArea?.type === 'rectangle' && selectionArea.x !== undefined && (
                      <Rect
                        key="selection-rect"
                        ref={selectionRectRef}
                        x={selectionArea.x}
                        y={selectionArea.y}
                        width={selectionArea.width}
                        height={selectionArea.height}
                        stroke="#ef4444"
                        strokeWidth={2}
                        dash={[5, 5]}
                        fill="rgba(239, 68, 68, 0.2)"
                        onMouseDown={handleSelectionMouseDown}
                        onTouchStart={handleSelectionMouseDown}
                      />
                    )}
                    {/* 確定したフリーハンド線 */}
                    {selectionArea?.type === 'freehand' && drawingLines.length > 0 && (
                      <Line
                        key="selection-line"
                        ref={selectionLineRef}
                        points={drawingLines[0]}
                        stroke="#ef4444"
                        strokeWidth={2}
                        tension={0.5}
                        lineCap="round"
                        lineJoin="round"
                        closed
                        fill="rgba(239, 68, 68, 0.2)"
                        onMouseDown={handleSelectionMouseDown}
                        onTouchStart={handleSelectionMouseDown}
                      />
                    )}
                    {/* 描画中の矩形 */}
                    {currentRect && currentRect.width > 0 && (
                      <Rect
                        x={currentRect.x}
                        y={currentRect.y}
                        width={currentRect.width}
                        height={currentRect.height}
                        stroke="#ef4444"
                        strokeWidth={2}
                        dash={[5, 5]}
                        fill="rgba(239, 68, 68, 0.1)"
                      />
                    )}
                    {/* 描画中のフリーハンド線 */}
                    {currentLine.length > 0 && (
                      <Line
                        points={currentLine}
                        stroke="#ef4444"
                        strokeWidth={2}
                        tension={0.5}
                        lineCap="round"
                        lineJoin="round"
                      />
                    )}
                    {/* Transformer（選択時のハンドル表示） */}
                    <Transformer
                      ref={transformerRef}
                      rotateEnabled={false}
                      keepRatio={true}
                      enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
                      boundBoxFunc={(oldBox, newBox) => {
                        // 最小サイズを制限
                        if (newBox.width < 20 || newBox.height < 20) {
                          return oldBox;
                        }
                        return newBox;
                      }}
                    />
                  </Layer>
                </Stage>
              ) : (
                <p className="text-gray-400">画像を読み込み中...</p>
              )}
            </div>
          </div>

          {/* 右側：結果表示パネル（編集履歴パネル風） */}
          <div className="flex-shrink-0 flex flex-col overflow-hidden" style={{ width: '400px', minWidth: '400px', maxWidth: '400px' }}>
            <div className="w-full flex-1 flex flex-col rounded-lg overflow-hidden min-h-0 border-2 border-gray-300 bg-white">
              <div className="text-gray-800 text-center py-2 px-4 font-semibold bg-gray-100 flex-shrink-0">
                編集履歴
              </div>

              <div className="w-full flex-1 overflow-y-auto p-3 bg-white min-h-0">
                {/* ローディング中のカード（履歴リストの上部に表示） */}
                {viewState === 'show_loading' && (
                  <div
                    className="flex gap-4 rounded-lg p-3 w-full mb-3"
                    style={{
                      backgroundColor: '#fff7ed',
                      border: '3px solid #f97316',
                      minHeight: '160px',
                      maxHeight: '160px',
                    }}
                  >
                    <div className="flex-shrink-0 flex items-center justify-center bg-gray-100 rounded relative" style={{ width: '128px', height: '128px' }}>
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
                    </div>
                    <div className="flex-1 flex flex-col justify-center">
                      <p className="text-sm font-bold text-gray-800">処理中...</p>
                    </div>
                  </div>
                )}

                {/* 履歴リスト */}
                {afterImages.length > 0 ? (
                  <>
                    {afterImages.length > 0 && !isLoading && (
                      <p className="text-xs text-gray-500 mb-3 text-center">
                        クリックして過去の結果を表示
                      </p>
                    )}
                    <div className="w-full space-y-3">
                      {[...afterImages].reverse().map((historyImage, index) => {
                        const actualIndex = afterImages.length - 1 - index;
                        const isLatest = actualIndex === afterImages.length - 1;
                        const isSelected = showAfter && (selectedAfterImage === historyImage.id ||
                          (selectedAfterImage === undefined && isLatest));

                        return (
                          <div
                            key={historyImage.id}
                            onClick={() => {
                              dispatch(floorPlanExtractionActions.setSelectedAfterImage(historyImage.id));
                              setShowAfter(true);
                            }}
                            className="flex gap-4 rounded-lg cursor-pointer transition-all p-3"
                            style={
                              isSelected
                                ? {
                                    backgroundColor: '#fff7ed',
                                    border: '3px solid #f97316',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                    minHeight: '160px',
                                    maxHeight: '160px',
                                  }
                                : {
                                    backgroundColor: '#ffffff',
                                    border: '2px solid #e5e7eb',
                                    minHeight: '160px',
                                    maxHeight: '160px',
                                  }
                            }
                          >
                            {/* サムネイル画像 */}
                            <div className="flex-shrink-0 flex items-center justify-center bg-gray-100 rounded relative" style={{ width: '128px', height: '128px' }}>
                              <img
                                src={historyImage.serverURI}
                                alt={`抜き出し結果 ${actualIndex + 1}`}
                                className="w-full h-full object-contain"
                              />
                              {/* 最新バッジ */}
                              {isLatest && (
                                <div
                                  className="absolute top-2 right-2 text-xs font-bold px-2 py-1 rounded-full text-white shadow-lg border-2 border-white"
                                  style={{ backgroundColor: '#6b7280' }}
                                >
                                  最新
                                </div>
                              )}
                            </div>

                            {/* 情報エリア */}
                            <div className="flex-1 flex flex-col justify-center">
                              <p className="text-sm font-bold text-gray-800 mb-2">
                                間取り抜き出し #{actualIndex + 1}
                              </p>
                              <p className="text-xs text-gray-600 mb-1">
                                スタイル: {historyImage.style === 'normal' ? '元画像のまま' :
                                          historyImage.style === 'colored' ? '色付け' :
                                          historyImage.style === 'handdrawn' ? '手書き風' :
                                          historyImage.style === '3d' ? '3D' : historyImage.style}
                              </p>
                              {historyImage.createdAt && (
                                <p className="text-xs text-gray-500">
                                  {(() => {
                                    const date = new Date(historyImage.createdAt);
                                    const year = date.getFullYear();
                                    const month = (date.getMonth() + 1).toString().padStart(2, '0');
                                    const day = date.getDate().toString().padStart(2, '0');
                                    const hours = date.getHours().toString().padStart(2, '0');
                                    const minutes = date.getMinutes().toString().padStart(2, '0');
                                    return `${year}/${month}/${day} ${hours}:${minutes}`;
                                  })()}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {/* 元画像カード（リストの一番下に表示） */}
                      {sourceImage && (
                        <div
                          onClick={() => setShowAfter(false)}
                          className="flex gap-4 rounded-lg cursor-pointer transition-all p-3"
                          style={
                            !showAfter
                              ? {
                                  backgroundColor: '#fff7ed',
                                  border: '3px solid #f97316',
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                  minHeight: '160px',
                                  maxHeight: '160px',
                                }
                              : {
                                  backgroundColor: '#ffffff',
                                  border: '2px solid #e5e7eb',
                                  minHeight: '160px',
                                  maxHeight: '160px',
                                }
                          }
                        >
                          <div className="flex-shrink-0 flex items-center justify-center bg-gray-100 rounded relative" style={{ width: '128px', height: '128px' }}>
                            <img
                              src={sourceImage.uri}
                              alt="元画像"
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <div className="flex-1 flex flex-col justify-center">
                            <p className="text-sm font-bold text-gray-800 mb-2">元画像</p>
                            <p className="text-xs text-gray-500">アップロード画像</p>
                          </div>
                        </div>
                      )}
                    </div>

                  </>
                ) : !isLoading ? (
                  // 履歴がない場合の表示
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <p className="text-base font-medium mb-2">編集履歴はまだありません</p>
                      <p className="text-sm">画像をアップロードして、範囲を選択し</p>
                      <p className="text-sm">抜き出しを実行すると</p>
                      <p className="text-sm">履歴がここに表示されます</p>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* 固定フッター：ダウンロードボタン */}
              {!isLoading && (afterImageData || sourceImage) && (
                <div className="flex-shrink-0 p-3 border-t border-gray-200 bg-white">
                  <button
                    onClick={handleOpenDownloadModal}
                    disabled={(showAfter && !afterImageData) || (!showAfter && !sourceImage)}
                    className={`w-full px-3 py-2 rounded-md text-sm font-medium ${
                      (showAfter && afterImageData) || (!showAfter && sourceImage)
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    選択中の画像をダウンロード
                  </button>
                </div>
              )}
            </div>
          </div>
      </div>
    </div>
  );
};

export default connect(mapStateToProps)(FloorPlanFormSome);
